import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { expectedVersionFor, labelFor, type RequiredConsentScope } from '@/constants/legal';
import { recordConsent } from '@/services/api.client';
import { publishConsentOutdated, subscribeConsentOutdated } from '@/services/consent-events';

/**
 * ConsentReacceptanceModal — hard-blocking dialog shown when the server tells
 * us (via X-Consent-Outdated) that the user has not accepted the current
 * version of one or more required documents.
 *
 * Mounted once at App.tsx. Subscribes to the consent-events bridge published
 * by the api.client response interceptor. Renders nothing while the
 * outdated-scope list is empty.
 *
 * On submit, posts one consent record per outdated scope and then clears
 * the bridge so the modal closes. If the user declines, they can sign out;
 * they remain signed in but every navigation will re-open the modal.
 */
export function ConsentReacceptanceModal() {
  const { isAuthenticated, signOut } = useAuth();
  const [outdated, setOutdated] = useState<RequiredConsentScope[]>([]);
  const [accepted, setAccepted] = useState<Record<RequiredConsentScope, boolean>>({
    privacy_policy: false,
    terms_of_service: false,
    age_18_plus: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return subscribeConsentOutdated((next) => {
      setOutdated(next);
      // Reset checkboxes whenever the server-reported scope list changes.
      setAccepted({
        privacy_policy: false,
        terms_of_service: false,
        age_18_plus: false,
      });
      setError(null);
    });
  }, []);

  // Don't render at all for unauthenticated users — prevents the modal
  // appearing during the brief window between login response and the
  // AuthContext settling.
  if (!isAuthenticated || outdated.length === 0) {
    return null;
  }

  const allAccepted = outdated.every((scope) => accepted[scope]);

  async function handleSubmit() {
    if (!allAccepted) return;
    setSubmitting(true);
    setError(null);
    try {
      // Record each outdated consent in sequence — failure on any one
      // surfaces an error and leaves the modal open.
      for (const scope of outdated) {
        await recordConsent({
          scope,
          version: expectedVersionFor(scope),
          granted: true,
        });
      }
      // Clear the bridge — the next request will refresh the header anyway,
      // but optimistic dismissal feels right.
      publishConsentOutdated(undefined);
    } catch (err) {
      setError(
        err instanceof Object && 'message' in err
          ? String((err as { message: string }).message)
          : 'We could not record your acceptance. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDecline() {
    // Per ToS §13: the user may sign out; continued use after the new
    // version's effective date constitutes acceptance, so we sign them
    // out cleanly rather than leaving them signed-in-but-blocked.
    await signOut();
    publishConsentOutdated(undefined);
  }

  return (
    <Dialog open={outdated.length > 0} onOpenChange={() => undefined}>
      <DialogContent
        // Hide the default close affordance — the modal is intentionally
        // hard-blocking. Sign-out is the only escape.
        className="sm:max-w-lg [&>button]:hidden"
      >
        <DialogHeader>
          <DialogTitle>We have updated our terms</DialogTitle>
          <DialogDescription>
            Please take a moment to review and accept the updated documents below before continuing.
            This is required by the Kenya Data Protection Act.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {outdated.map((scope) => {
            if (scope === 'age_18_plus') {
              return (
                <label
                  key={scope}
                  className="flex items-start gap-3 rounded-md border border-[var(--border-default)] bg-[var(--bg-surface)] p-3 text-sm"
                >
                  <Checkbox
                    checked={accepted[scope]}
                    onCheckedChange={(v) =>
                      setAccepted((prev) => ({ ...prev, [scope]: v === true }))
                    }
                  />
                  <span>I confirm that I am at least 18 years old.</span>
                </label>
              );
            }

            const href = scope === 'privacy_policy' ? '/privacy' : '/terms';
            return (
              <label
                key={scope}
                className="flex items-start gap-3 rounded-md border border-[var(--border-default)] bg-[var(--bg-surface)] p-3 text-sm"
              >
                <Checkbox
                  checked={accepted[scope]}
                  onCheckedChange={(v) => setAccepted((prev) => ({ ...prev, [scope]: v === true }))}
                />
                <span>
                  I have read and accept the updated{' '}
                  <Link to={href} target="_blank" className="underline underline-offset-4">
                    {labelFor(scope)}
                  </Link>
                  .
                </span>
              </label>
            );
          })}
        </div>

        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={() => void handleDecline()} disabled={submitting}>
            Decline and sign out
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!allAccepted || submitting}>
            {submitting ? 'Recording…' : 'Accept and continue'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
